import { Log } from '@devanjs/log';
import { services } from '../services';
import type { PossiblyAsyncIntegration } from '../types/asyncIntegration';
import type { Integration, IntegrationConfig } from '../types/integration';
import type { IntegrationsStore } from '../types/integrationsStore';

export type UninstallOptions = {
  preUninstallHook?: () => Promise<any>;
  postUninstallHook?: () => Promise<any>;
};

export const uninstall = async <
  I extends Integration,
  C extends IntegrationConfig<I>,
  Integrations extends Record<string, PossiblyAsyncIntegration>,
>(
  store: IntegrationsStore<Integrations>,
  integration: I,
  config: (config: C) => C = (c) => c,
  options?: UninstallOptions,
) => {
  Log.tag('integrations').space().deepOrange('Uninstalling').info(integration.code);

  if (options?.preUninstallHook) {
    await options?.preUninstallHook();
  }

  Log.tag('integrations').space().deepOrange(integration.code).info('Teardown');

  await integration.teardown({
    config: config(integration.config()),
  });

  /**
   * Clean up any routes that were added by the integration.
   */
  const installation = store.installations[integration.code];

  if (installation) {
    if (services.router) {
      installation.routes.forEach((route) => {
        Log.tag('integrations')
          .space()
          .deepOrange(integration.code)
          .orange('Route')
          .info(route.path);

        services.router?.removeRoute(route.name || '');
      });
    } else {
      Log.red('Integrations').info('Router not found');
    }

    if (services.menu) {
      installation.menu.forEach((item) => {
        Log.tag('integrations')
          .space()
          .deepOrange(integration.code)
          .orange('Menu')
          .info(item.title);

        services.menu?.remove(item);
      });
    } else {
      Log.red('Integrations').info('Menu not found');
    }

    store.extensions
      .filter((extension) => {
        return (
          extension.target === installation.integration.code ||
          extension.source.code === installation.integration.code
        );
      })
      .forEach((extension) => {
        const targetInstallation = store.installations[extension.target];

        if (targetInstallation !== undefined) {
          extension.teardown();
        }
      });
  }

  /**
   * Clean up the integration installation.
   */
  delete store.installations[integration.code];

  /**
   * Clean up any extensions that were added by the integration.
   */
  store.extensions.splice(
    0,
    store.extensions.length,
    ...store.extensions.filter((i) => i.source.code !== integration.code),
  );

  if (options?.postUninstallHook) {
    await options?.postUninstallHook();
  }

  Log.tag('integrations').space().deepOrange('Uninstalled').info(integration.code);
};
